import type { ReactNode } from 'react'
import { Button, Dialog, DialogContent, DialogTitle } from '@teach/ui'
import butterfly from '@/assets/butterfly.svg'
import { useMessages } from '@/i18n'
import { MCP_URL } from '../guideContent'
import { CLAUDE_HOME_URL, CLAUDE_NEW_CHAT_URL, claudeConnectUrl } from '../lib/claudeLinks'
import { messages } from '../messages'
import { ClaudeIcon } from './BrandIcons'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const LINK = 'underline decoration-foreground/30 underline-offset-4 hover:text-foreground'
const TEXT = 'mt-1 text-sm leading-relaxed text-muted-foreground'
// Без обёртки-компонента: `Button asChild` передаёт классы прямо элементу,
// и обёртка без проброса пропсов съедала бы оформление кнопки.
const EXTERNAL = { target: '_blank', rel: 'noopener noreferrer' } as const

const Step = ({ n, title, children }: { n: number; title: ReactNode; children: ReactNode }) => (
  <li className="border-b border-foreground/10 py-5 first:pt-0 last:border-0 last:pb-0">
    <span className="flex size-8 items-center justify-center rounded-full bg-foreground/10 text-sm font-medium">
      {n}
    </span>
    <h3 className="mt-4 flex items-center gap-2 text-base font-medium">{title}</h3>
    {children}
  </li>
)

// Знак студии — тот же ромб, что в иконке вкладки браузера.
const Logo = () => <img src="/favicon.svg" alt="" aria-hidden="true" className="size-5" />

/** Подключение к Claude за три шага — по образцу Higgsfield: войти, добавить
 *  коннектор одной ссылкой, начать чат. Справа кадр под анимацию-демо. */
export const ClaudeConnectDialog = ({ open, onOpenChange }: Props) => {
  const m = useMessages(messages)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-[980px] gap-5 overflow-y-auto p-4 sm:rounded-3xl">
        <DialogTitle className="px-4 pr-12 pt-2 text-lg font-medium">
          {m.claudeDialogTitle}
        </DialogTitle>
        <div className="grid items-start gap-8 rounded-2xl bg-foreground/5 p-6 md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
          <ol>
            <Step n={1} title={m.claudeStep1Title}>
              <p className={TEXT}>
                {m.claudeStep1Lead}{' '}
                <a href={CLAUDE_HOME_URL} {...EXTERNAL} className={LINK}>
                  claude.ai
                </a>{' '}
                {m.claudeStep1Tail}
              </p>
            </Step>
            <Step
              n={2}
              title={
                <>
                  {m.claudeStep2Title} <Logo /> OA Studio
                </>
              }
            >
              <p className={TEXT}>
                {m.claudeStep2Lead}{' '}
                <a href={MCP_URL} {...EXTERNAL} className={`${LINK} whitespace-nowrap`}>
                  {MCP_URL}
                </a>{' '}
                {m.claudeStep2Tail}
              </p>
              <Button
                asChild
                variant="secondary"
                size="lg"
                className="mt-4 bg-foreground/15 text-foreground hover:bg-foreground/20"
              >
                <a href={claudeConnectUrl()} {...EXTERNAL}>
                  <Logo />
                  {m.claudeStep2Button}
                </a>
              </Button>
            </Step>
            <Step n={3} title={m.claudeStep3Title}>
              <p className={TEXT}>{m.claudeStep3Text}</p>
              <Button
                asChild
                size="lg"
                className="mt-4 bg-claude text-claude-foreground hover:bg-claude-hover"
              >
                <a href={CLAUDE_NEW_CHAT_URL} {...EXTERNAL}>
                  <ClaudeIcon />
                  {m.claudeStep3Button}
                </a>
              </Button>
            </Step>
          </ol>
          {/* Сюда встанет анимация-демо (как у Higgsfield), пока — иллюстрация. */}
          <div className="hidden aspect-video place-items-center rounded-2xl bg-foreground/5 md:grid">
            <img src={butterfly} alt="" aria-hidden="true" draggable={false} className="size-28" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
